import React, { useEffect, useMemo, useRef, useState } from "react";
import { SvgComponent } from "@commons/Componentes/SvgComponent/SvgComponent";
import { RevisionSeccion } from "./RevisionSeccion";
import { RevisionAmpliacion } from "./RevisionAmpliacion";
import { RevisionDiferencias } from "./RevisionDiferencias";
import { hayCambios } from "./diferenciasTexto";
import { CORRECCION_APLICADA, CORRECCION_PENDIENTE } from "../../Services/conocimientoIaService";

const TONO_CORRECCION = {
  [CORRECCION_PENDIENTE]: "pendiente",
  [CORRECCION_APLICADA]: "validada"
};

// Una sola referencia para "sin documentos": un [] nuevo en cada render reiniciaría las ediciones.
const SIN_DOCUMENTOS = [];

const textoInicial = (fragmento) => fragmento.FragmentoCorregido || fragmento.Texto || "";

const Casilla = ({ id, etiqueta, ayuda, checked, onChange, disabled }) => (
  <label className="citrus-ia-casilla citrus-ia-opcion" htmlFor={id} title={ayuda}>
    <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />
    <span>{etiqueta}</span>
  </label>
);

const EtiquetasDocumento = ({ documento }) => (
  <>
    {documento.EsCaso && <span className="citrus-gpt-tag citrus-gpt-tag--validada">Respuesta validada</span>}
    {documento.EsAmpliacion && <span className="citrus-gpt-tag citrus-gpt-tag--modelo">Ampliación de conocimiento</span>}
    {!documento.FueCitado && <span className="citrus-gpt-tag citrus-gpt-tag--neutra">Recuperada, no citada</span>}
    {documento.Fragmentos.length > 1 && (
      <span className="citrus-gpt-tag citrus-gpt-tag--neutra">{documento.Fragmentos.length} fragmentos</span>
    )}
  </>
);

const detalleCorreccion = (fragmento) => {
  const partes = [];
  if (fragmento.CorreccionUsuario) partes.push(`Corregido por ${fragmento.CorreccionUsuario} el ${fragmento.FechaCorreccion}`);
  if (fragmento.AplicadaPor) partes.push(`Aplicado por ${fragmento.AplicadaPor} el ${fragmento.FechaAplicada}`);
  return partes.join(" · ");
};

const Fragmento = ({ fragmento, editable, valor, onChange, maxLargo, bloqueado, puedeMarcar, onMarcar, marcando }) => {
  const original = fragmento.Texto || "";
  const cambiado = editable && hayCambios(original, valor);
  const tieneCorreccion = Boolean(fragmento.FragmentoCorregido) && hayCambios(original, fragmento.FragmentoCorregido);
  const detalle = detalleCorreccion(fragmento);

  return (
    <div className="citrus-ia-fragmento">
      <div className="citrus-ia-fragmento__meta">
        {fragmento.FueCitada ? "Citado" : "Recuperado"}
        {fragmento.Coincidencia != null && ` · coincidencia ${fragmento.Coincidencia}%`}
        {fragmento.EstadoCorreccion && (
          <span
            className={`citrus-gpt-tag citrus-gpt-tag--${TONO_CORRECCION[fragmento.EstadoCorreccion] || "neutra"}`}
            title={detalle || undefined}
          >
            {fragmento.EstadoCorreccionDesc}
          </span>
        )}
        {detalle && <span className="citrus-ia-fragmento__quien">{detalle}</span>}
        {puedeMarcar && fragmento.EstadoCorreccion === CORRECCION_PENDIENTE && (
          <button
            type="button"
            className="citrus-ia-fragmento__marcar"
            onClick={() => onMarcar(fragmento.Id)}
            disabled={marcando || bloqueado}
            title="Use esto cuando la corrección ya esté hecha en el proyecto Citrus Web de la documentación"
          >
            <SvgComponent iconName="check" width="14px" height="14px" fill="currentColor" />
            {marcando ? "Marcando..." : "Marcar como aplicada en el proyecto"}
          </button>
        )}
      </div>

      {editable ? (
        <>
          <textarea
            className="citrus-ia-fragmento__editor"
            value={valor}
            maxLength={maxLargo}
            onChange={(e) => onChange(e.target.value)}
            disabled={bloqueado}
            aria-label="Texto corregido del fragmento"
          />
          <div className="citrus-ia-fragmento__pie">
            <span>{valor.length} / {maxLargo}</span>
            {cambiado && (
              <details className="citrus-ia-fragmento__original">
                <summary>Ver texto original completo</summary>
                <pre>{original}</pre>
              </details>
            )}
          </div>
          {cambiado && <RevisionDiferencias original={original} corregido={valor} />}
        </>
      ) : (
        <>
          <pre className="citrus-ia-fragmento__texto">{original}</pre>
          {tieneCorreccion && <RevisionDiferencias original={original} corregido={fragmento.FragmentoCorregido} />}
        </>
      )}
    </div>
  );
};

export const RevisionFuentes = ({
  idSec,
  documentos = SIN_DOCUMENTOS,
  moduloRespuesta,
  puedeRevisar,
  maxLargo,
  onGuardarCorrecciones,
  onMarcarAplicadas,
  onAmpliado,
  onMensaje,
  onFallo
}) => {
  const [corregir, setCorregir] = useState(false);
  const [ampliar, setAmpliar] = useState(false);
  const [ediciones, setEdiciones] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [marcando, setMarcando] = useState(null);
  const iniciales = useRef({});
  const [abiertos, setAbiertos] = useState({});

  const alternar = (clave) => setAbiertos((actual) => ({ ...actual, [clave]: !actual[clave] }));

  const corregibles = useMemo(
    () => documentos.filter((d) => d.PuedeCorregirse).flatMap((d) => d.Fragmentos),
    [documentos]
  );

  // Al refrescar se conservan las ediciones sin guardar, salvo que ese fragmento cambió en el servidor.
  useEffect(() => {
    const anteriores = iniciales.current;
    setEdiciones((actual) => Object.fromEntries(corregibles.map((f) => {
      const guardado = textoInicial(f);
      const editado = actual[f.Id];
      const sinGuardar = editado !== undefined && editado !== anteriores[f.Id];
      return [f.Id, sinGuardar && anteriores[f.Id] === guardado ? editado : guardado];
    })));
    iniciales.current = Object.fromEntries(corregibles.map((f) => [f.Id, textoInicial(f)]));
  }, [corregibles]);

  const marcar = async (fuenteId) => {
    if (marcando) return;
    setMarcando(fuenteId);
    try {
      await onMarcarAplicadas([fuenteId]);
    } finally {
      setMarcando(null);
    }
  };

  const pendientes = corregibles.filter((f) => ediciones[f.Id] !== undefined && ediciones[f.Id] !== textoInicial(f));

  const guardar = async () => {
    if (!pendientes.length || guardando) return;
    setGuardando(true);
    try {
      await onGuardarCorrecciones(pendientes.map((f) => ({ FuenteId: f.Id, Texto: ediciones[f.Id] })));
    } finally {
      setGuardando(false);
    }
  };

  const acciones = puedeRevisar && (
    <div className="citrus-ia-opciones">
      <Casilla
        id="ia-corregir-documentacion"
        etiqueta="Corregir documentación"
        ayuda={corregibles.length
          ? "Corrija el texto de los fragmentos de donde respondió. Se guarda para aplicarlo después en el proyecto de la documentación"
          : "Esta respuesta no tiene fragmentos de documentación que corregir"}
        checked={corregir}
        onChange={setCorregir}
        disabled={guardando || corregibles.length === 0}
      />
      <Casilla
        id="ia-ampliar-conocimiento"
        etiqueta="Ampliar conocimiento de la IA"
        ayuda="Agregue conocimiento que la documentación no tiene. Se publica en un archivo de ampliación que el asistente consulta"
        checked={ampliar}
        onChange={setAmpliar}
      />
    </div>
  );

  return (
    <RevisionSeccion titulo="Fuentes utilizadas" icono="attach-file">
      {acciones}

      {ampliar && (
        <RevisionAmpliacion
          idSec={idSec}
          documentos={documentos.filter((d) => d.PuedeAmpliarse)}
          moduloRespuesta={moduloRespuesta}
          maxLargo={maxLargo}
          onMensaje={onMensaje}
          onFallo={onFallo}
          onAmpliado={onAmpliado}
        />
      )}

      {corregir && corregibles.length > 0 && (
        <p className="citrus-ia-campo__nota">Pulse un archivo para ver y corregir sus fragmentos.</p>
      )}

      {documentos.length ? (
        <div className="citrus-ia-documentos">
          {documentos.map((documento) => {
            const editable = corregir && documento.PuedeCorregirse;
            const abierto = Boolean(abiertos[documento.Clave]);
            const sinGuardar = pendientes.filter((f) => documento.Fragmentos.includes(f)).length;
            const porAplicar = documento.Fragmentos.filter((f) => f.EstadoCorreccion === CORRECCION_PENDIENTE).length;

            return (
              <div key={documento.Clave} className={`citrus-ia-documento${abierto ? " citrus-ia-documento--abierto" : ""}`}>
                <button
                  type="button"
                  className="citrus-ia-documento__cabecera"
                  onClick={() => alternar(documento.Clave)}
                  aria-expanded={abierto}
                  title={abierto ? "Ocultar los fragmentos" : "Ver los fragmentos de este archivo"}
                >
                  <SvgComponent iconName={abierto ? "down" : "right"} width="16px" height="16px" fill="currentColor" />
                  <SvgComponent iconName="single-note" width="16px" height="16px" fill="currentColor" />
                  <span className="citrus-ia-documento__nombre">{documento.Nombre}</span>
                  <EtiquetasDocumento documento={documento} />
                  {porAplicar > 0 && (
                    <span className="citrus-gpt-tag citrus-gpt-tag--pendiente">
                      {porAplicar} corrección(es) por aplicar
                    </span>
                  )}
                  {sinGuardar > 0 && (
                    <span className="citrus-gpt-tag citrus-gpt-tag--riesgo">{sinGuardar} sin guardar</span>
                  )}
                </button>

                {abierto && documento.Fragmentos.map((fragmento) => (
                  <Fragmento
                    key={fragmento.Id}
                    fragmento={fragmento}
                    editable={editable}
                    valor={ediciones[fragmento.Id] ?? textoInicial(fragmento)}
                    onChange={(valor) => setEdiciones((actual) => ({ ...actual, [fragmento.Id]: valor }))}
                    maxLargo={maxLargo}
                    bloqueado={guardando}
                    puedeMarcar={puedeRevisar}
                    onMarcar={marcar}
                    marcando={marcando === fragmento.Id}
                  />
                ))}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="citrus-gpt-msg__aviso">Ninguna. La respuesta no tiene respaldo en la documentación.</p>
      )}

      {corregir && corregibles.length > 0 && (
        <div className="citrus-ia-seccion__pie">
          <span className="citrus-ia-campo__nota">
            {pendientes.length
              ? `${pendientes.length} fragmento(s) con cambios sin guardar.`
              : "Edite el texto de los fragmentos que estén mal."}
          </span>
          <button
            type="button"
            className="citrus-ia-accion citrus-ia-accion--naranja"
            onClick={guardar}
            disabled={guardando || pendientes.length === 0}
          >
            <SvgComponent iconName="save" width="18px" height="18px" fill="currentColor" />
            <span>{guardando ? "Guardando..." : "Guardar correcciones"}</span>
          </button>
        </div>
      )}
    </RevisionSeccion>
  );
};
